import React, { useState, useEffect, useRef } from "react";
import "./AgentNotifications.css";
import { websocketService } from "../../services/websocket.service";
import { updateUserActiveStatus } from "../../services/authService";
import api from "../../services/api";
import { jwtDecode } from "jwt-decode"; // named export

const AgentNotifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [isActive, setIsActive] = useState(false);
    const [userId, setUserId] = useState(null);
    const [hasInteracted, setHasInteracted] = useState(false);

    // Twilio call handling
    const [device, setDevice] = useState(null);
    const [incomingCall, setIncomingCall] = useState(null);
    const [connection, setConnection] = useState(null);
    const [twilioStatus, setTwilioStatus] = useState("Initializing...");

    const isActiveRef = useRef(isActive);
    useEffect(() => {
        isActiveRef.current = isActive;
    }, [isActive]);

    const handleToggleActive = async () => {
        try {
            await updateUserActiveStatus(userId, !isActive);
            setIsActive(!isActive);
        } catch (error) {
            console.error("Error updating active status:", error);
            setIsActive(isActive);
        }
    };

    const markAsRead = (id) => {
        setNotifications((prev) =>
            prev.map((notif) => (notif.id === id ? { ...notif, read: true } : notif))
        );
    };

    const clearAll = () => {
        setNotifications([]);
    };

    const testWebSocket = async () => {
        try {
            console.log("Testing WebSocket connection...");
            await api.post("/test-websocket");
        } catch (error) {
            console.error("Error testing WebSocket:", error);
        }
    };

    const answerCall = () => {
        if (incomingCall) {
            incomingCall.accept();
            setConnection(incomingCall);
            setIncomingCall(null);
            setTwilioStatus("Call in progress");
        }
    };

    const hangUpCall = () => {
        if (connection) {
            connection.disconnect();
            setConnection(null);
            setTwilioStatus("Call ended");
        }
    };

    const handleInteraction = () => setHasInteracted(true);
    useEffect(() => {
        document.addEventListener("click", handleInteraction);
        return () => document.removeEventListener("click", handleInteraction);
    }, []);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setUserId(decoded.userId);
                setIsActive(decoded.isActive || false);
            } catch (error) {
                console.error("Error decoding token:", error);
            }
        }
        if (Notification.permission !== "granted") {
            Notification.requestPermission();
        }
    }, []);

    useEffect(() => {
        const unsubscribe = websocketService.subscribe(async (messageString) => {
            try {
                const msg =
                    typeof messageString === "string"
                        ? JSON.parse(messageString)
                        : messageString;

                if (msg.type === "INCOMING_CALL") {
                    const callSid = msg.data.callSid;
                    let displayNumber = msg.data.from;

                    if (callSid) {
                        try {
                            const response = await api.get(`/dialer/call-history?callSid=${callSid}`);
                            const record = response.data.callHistory;
                            if (record && record.leadPhone) {
                                displayNumber = record.leadPhone;
                            }
                        } catch (lookupError) {
                            console.error("Call history lookup error:", lookupError);
                        }
                    }

                    const newNotification = {
                        id: Date.now(),
                        tenantId: "system",
                        userId: userId,
                        type: "INCOMING_CALL",
                        title: "Incoming Call",
                        message: `Call from ${displayNumber} (${msg.data.callerLocation})`,
                        read: false,
                        createdAt: msg.data.timestamp,
                    };
                    setNotifications((prev) => [newNotification, ...prev]);

                    if (Notification.permission === "granted") {
                        new Notification("Incoming Call", {
                            body: `Call from ${displayNumber}\nLocation: ${msg.data.callerLocation}`,
                        });
                    }

                    if (hasInteracted) {
                        const audio = new Audio("/notification-sound.mp3");
                        audio.play().catch((error) =>
                            console.warn("Could not play notification sound:", error)
                        );
                    }
                } else if (
                    msg.type === "TEST_NOTIFICATION" ||
                    msg.type === "NEW_NOTIFICATION"
                ) {
                    const newNotification = {
                        id: Date.now(),
                        type: msg.type,
                        title: msg.payload.title || msg.type,
                        message: msg.payload.message,
                        createdAt: msg.payload.createdAt || new Date().toISOString(),
                        read: false,
                    };
                    setNotifications((prev) => [newNotification, ...prev]);
                }
            } catch (error) {
                console.error("Error processing message:", error);
            }
        });

        return () => {
            unsubscribe();
        };
    }, [userId, hasInteracted]);

    useEffect(() => {
        api
            .get("/twilio/token")
            .then((res) => res.data)
            .then((data) => {
                const twilioToken = data.token;
                const twilioDevice = new window.Twilio.Device(twilioToken, { debug: true });

                twilioDevice.on("ready", () => {
                    setTwilioStatus("Ready for incoming calls");
                });
                twilioDevice.on("error", (error) => {
                    console.error("Twilio.Device error:", error);
                    setTwilioStatus("Error initializing Twilio Device");
                });
                twilioDevice.on("incoming", (conn) => {
                    if (!isActiveRef.current) {
                        conn.reject();
                        setTwilioStatus("Call rejected: agent inactive");
                        return;
                    }
                    setIncomingCall(conn);
                    setTwilioStatus(`Incoming call from ${conn.parameters.From}`);
                });
                twilioDevice.on("cancel", () => {
                    setIncomingCall(null);
                    setTwilioStatus("Call canceled");
                });

                setDevice(twilioDevice);
            })
            .catch((err) => {
                console.error("Failed to fetch Twilio token:", err);
                setTwilioStatus("Error fetching token");
            });
    }, []);

    const renderSoundWarning = () => {
        if (!hasInteracted) {
            return (
                <div className="sound-warning">
                    <p>Click anywhere on the page to enable notification sounds</p>
                </div>
            );
        }
        return null;
    };

    const renderIncomingCallAlert = () => {
        if (!incomingCall) return null;
        return (
            <div className="incoming-call-alert">
                <div className="incoming-call-content">
                    <h3>Incoming Call</h3>
                    <p>From: {incomingCall.parameters.From}</p>
                    <div className="incoming-call-actions">
                        <button className="accept-call-btn" onClick={answerCall}>
                            Answer
                        </button>
                        <button
                            className="reject-call-btn"
                            onClick={() => {
                                incomingCall.reject();
                                setIncomingCall(null);
                                setTwilioStatus("Call rejected");
                            }}
                        >
                            Reject
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        // .notifications-container no longer has a fixed max-width (see CSS changes)
        <div className="notifications-container">
            {/* Remove the ENTIRE top-bar, as requested */}

            {/* Move notifications-header to the top. */}
            <div className="notifications-header">
                <h2>Agent Command Center</h2>
                <div className="header-controls">
                    <div className="active-toggle-container">
            <span className={`status-label ${!isActive ? "active" : ""}`}>
              Inactive
            </span>
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={isActive}
                                onChange={handleToggleActive}
                            />
                            <span className="slider round"></span>
                        </label>
                        <span className={`status-label ${isActive ? "active" : ""}`}>
              Active
            </span>
                    </div>
                   {/* <button
                        className="test-ws-btn"
                        onClick={testWebSocket}
                        style={{ marginRight: "10px" }}
                    >
                        Test WebSocket
                    </button>*/}
                   {/* <button className="clear-all-btn" onClick={clearAll}>
                        Clear All
                    </button>*/}
                </div>
            </div>

            {/* Place twilio-status directly under notifications-header */}
            <div className="twilio-status" style={{ padding: "10px", background: "#f0f0f0" }}>
                <p>{twilioStatus}</p>
            </div>

            {/* KPI card rows fill full width now */}
            <div className="agent-command-center-section">
                <div className="kpi-cards-row">
                    <div className="kpi-card kpi-white">
                        <div className="kpi-title">Calls Taken</div>
                        <div className="kpi-value">55</div>
                    </div>
                    <div className="kpi-card kpi-white">
                        <div className="kpi-title">Deals Won</div>
                        <div className="kpi-value">4</div>
                    </div>
                    <div className="kpi-card kpi-white">
                        <div className="kpi-title">Closing Rate</div>
                        <div className="kpi-value">7%</div>
                    </div>
                    <div className="kpi-card kpi-white">
                        <div className="kpi-title">Hours Active</div>
                        <div className="kpi-value">4:21</div>
                    </div>
                </div>
                <div className="kpi-cards-row">
                    <div className="kpi-card kpi-gray">
                        <div className="kpi-title">Disconnected</div>
                        <div className="kpi-value">15</div>
                    </div>
                    <div className="kpi-card kpi-blue">
                        <div className="kpi-title">Reschedule</div>
                        <div className="kpi-value">8</div>
                    </div>
                    <div className="kpi-card kpi-yellow">
                        <div className="kpi-title">Follow-Up Needed</div>
                        <div className="kpi-value">10</div>
                    </div>
                    <div className="kpi-card kpi-green">
                        <div className="kpi-title">Deal Closed - Won</div>
                        <div className="kpi-value">12</div>
                    </div>
                </div>
                <div className="kpi-cards-row">
                    <div className="kpi-card kpi-pink">
                        <div className="kpi-title">Deal Closed - Lost</div>
                        <div className="kpi-value">5</div>
                    </div>
                    <div className="kpi-card kpi-peach">
                        <div className="kpi-title">Price Objection</div>
                        <div className="kpi-value">7</div>
                    </div>
                    <div className="kpi-card kpi-lavender">
                        <div className="kpi-title">Needs More Info</div>
                        <div className="kpi-value">9</div>
                    </div>
                    <div className="kpi-card kpi-mint">
                        <div className="kpi-title">Unqualified Lead</div>
                        <div className="kpi-value">4</div>
                    </div>
                </div>
            </div>

            {renderSoundWarning()}
            {renderIncomingCallAlert()}

            {connection && (
                <div className="call-control">
                    <button onClick={hangUpCall}>Hang Up</button>
                </div>
            )}

            {/* Hide "No notifications available"; only show notifications if any */}
            {notifications.length > 0 && (
                <div className="notifications-list">
                    {notifications.map((notification) => (
                        <div
                            key={notification.id}
                            className={`notification-item ${
                                notification.read ? "read" : "unread"
                            }`}
                        >
                            <div className="notification-content">
                                <div className={`notification-type ${notification.type}`}>
                                    {notification.type}
                                </div>
                                <p className="notification-message">{notification.message}</p>
                                <span className="notification-time">
                  {new Date(notification.createdAt).toLocaleString()}
                </span>
                            </div>
                            {!notification.read && (
                                <button
                                    className="mark-read-btn"
                                    onClick={() => markAsRead(notification.id)}
                                >
                                    Mark as Read
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Mock Call History table at the bottom */}
            <div className="call-history-section">
                <h2>Call History</h2>
                <table className="call-history-table">
                    <thead>
                    <tr>
                        <th>Contact Name</th>
                        <th>Phone Number</th>
                        <th>Time of Call</th>
                        <th>Duration</th>
                        <th>Transcript</th>
                        <th>Action Taken</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td>John Doe</td>
                        <td>(123) 456-7890</td>
                        <td>10:15 AM</td>
                        <td>5 min</td>
                        <td><a href="#0">View Transcript</a></td>
                        <td>Follow-Up Needed</td>
                    </tr>
                    <tr>
                        <td>Jane Smith</td>
                        <td>(987) 654-3210</td>
                        <td>11:00 AM</td>
                        <td>8 min</td>
                        <td><a href="#0">View Transcript</a></td>
                        <td>Deal Closed - Won</td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AgentNotifications;
